import {
  JaneGoldTag,
  SpecialTag,
  getSpecialTagLabel,
  productPhotoMetadata,
} from '@iheartjane/menu';
import { Button, Card, Flex, Modal, Typography } from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import { useState } from 'react';

import type { WeightSelectorWeight } from '@jane/shared-ecomm/types';
import { useShouldShowGold } from '@jane/shared/hooks';
/* eslint-disable-next-line no-restricted-imports */
import type { MenuProduct } from '@jane/shared/models';
import { getDiscountedUnitPrice, getUnitPrice } from '@jane/shared/util';

import {
  PRICE_LOOKUP,
  ProductDetailPageLink,
} from '../legacyMenuProductCard/utils/productCardHelper';
import type { LinkProps } from '../legacyMenuProductCard/utils/productCardHelper';
import { useProductCardContext } from '../productCardProvider';
import { SelectWeightForm } from './selectWeightForm';
import { TextDetails } from './textDetails';

import styles from './bloomMenuProductCard.module.css';

interface BloomMenuProductCardProps {
  onAddToCartPressed: () => void;
  shouldDisplaySponsoredTag: boolean;
  weights: WeightSelectorWeight[];
}

export const BloomMenuProductCard = ({
  onAddToCartPressed,
  shouldDisplaySponsoredTag,
  weights,
}: BloomMenuProductCardProps) => {
  const {
    menuProduct: product,
    appMode,
    brand,
    breadcrumbs,
    searchState,
    fromAllSpecials,
    fromSpecialId,
    onCloseCart,
    pdpPath,
    hasNonStandardDefaultWeight,
    cartIsOpen,
    showOnlyWeights,
    defaultWeight,
    currentSpecial,
    store,
    janeGoldLabel,
  } = useProductCardContext();

  const productDetailPageLinkProps = {
    appMode,
    brand,
    breadcrumbs,
    cartIsOpen,
    defaultWeight: hasNonStandardDefaultWeight ? defaultWeight : undefined,
    product,
    searchState,
    store,
    fromAllSpecials,
    fromSpecialId,
    onCloseCart,
    pdpPath,
    bundleSpecialId:
      currentSpecial?.special_type === 'bundle' ? currentSpecial.id : undefined,
  } as Omit<LinkProps, 'children'>;

  const hasMultipleWeights =
    showOnlyWeights?.length !== 1 &&
    (product?.available_weights || []).length > 1;

  const isProductSpecial = currentSpecial?.special_type === 'product';
  const originalPrice = getUnitPrice(product as MenuProduct, defaultWeight);

  const discountedUnitPrice = getDiscountedUnitPrice(
    product as MenuProduct,
    defaultWeight
  );
  const unitPrice =
    isProductSpecial && discountedUnitPrice
      ? discountedUnitPrice
      : originalPrice;

  const { url } = productPhotoMetadata(product);

  const shouldShowGold = useShouldShowGold({ store });

  const janeGoldTagLabel = shouldShowGold ? janeGoldLabel : false;

  const specialTagLabel =
    store &&
    product &&
    getSpecialTagLabel({
      defaultWeight,
      product,
      store,
    });

  const specialApplies = specialTagLabel && currentSpecial;

  const defaultPriceId =
    product && product.amount ? '' : PRICE_LOOKUP[defaultWeight?.toUpperCase()];

  const [isWeightFormOpen, setIsWeightFormOpen] = useState(false);

  return (
    <>
      <ProductDetailPageLink {...productDetailPageLinkProps}>
        <div className={styles['styledCardContainer']}>
          <Card flat className={styles['styledCard']}>
            <Card.Image
              alt={product?.name || ''}
              src={url || ''}
              data-testid={'bloom-menu-product-card'}
            />
          </Card>
          <Flex
            position="absolute"
            right={8}
            top={8}
            gap={8}
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            {janeGoldTagLabel && <JaneGoldTag label={janeGoldTagLabel} />}
            {specialApplies && <SpecialTag label={specialTagLabel} />}
          </Flex>
          <TextDetails shouldDisplaySponsoredTag={shouldDisplaySponsoredTag} />
        </div>
      </ProductDetailPageLink>

      <Flex flexDirection="row" gap={4} alignItems="center" mb={8}>
        {isProductSpecial && discountedUnitPrice && (
          <Typography
            variant="body-semibold"
            data-testid="product-price"
            color="text-main"
          >
            {formatCurrency(discountedUnitPrice)}
          </Typography>
        )}
        {isProductSpecial && originalPrice && originalPrice !== unitPrice && (
          <Typography
            color="grays-mid"
            variant="small-body-regular"
            strikeThrough
          >
            {formatCurrency(originalPrice)}
          </Typography>
        )}
        {unitPrice !== undefined && !discountedUnitPrice && (
          <Typography as="span" variant="body-semibold" color="primary">
            {`${formatCurrency(unitPrice)}${
              defaultPriceId ? `/${defaultPriceId}` : ''
            }`}
          </Typography>
        )}
      </Flex>

      {hasMultipleWeights && (
        <Button
          label="Select weight"
          full
          onClick={() => setIsWeightFormOpen(true)}
        />
      )}

      {!hasMultipleWeights && (
        <Button label="Add to bag" full onClick={onAddToCartPressed} />
      )}
      <Modal
        open={isWeightFormOpen}
        onRequestClose={() => setIsWeightFormOpen(false)}
        overlayClose
        variant="dialogue"
      >
        <Modal.Header title="Add to bag" textAlign="center" />
        <Modal.Content>
          <SelectWeightForm
            weights={weights}
            onAddToCartPressed={onAddToCartPressed}
          />
        </Modal.Content>
      </Modal>
    </>
  );
};
